import React from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import agent from "../../../agent";
import SearchSelect from "../../CalculatorSplit/SearchSelect";
import { toRequest, validateOrganization } from "./validate";

const EMPTY = { companyName: "", bin: "", phone: "", email: "", katoCode: "" };

/**
 * Заведение и правка организации.
 *
 * Код (company_name) вводится только при создании и потом не меняется: от него
 * зависят наценки и закрытые направления партнёра и тип клиента в его заказах.
 */
const OrganizationDialog = ({ visible, organization, loading, onHide, onSubmit }) => {
  const isCreate = !organization;

  const [form, setForm] = React.useState(EMPTY);
  const [locality, setLocality] = React.useState({ kato: "" });
  const [options, setOptions] = React.useState([]);
  const [touched, setTouched] = React.useState(false);

  React.useEffect(() => {
    if (!visible) return;
    setTouched(false);
    setOptions([]);
    if (organization) {
      setForm({
        companyName: organization.companyName,
        bin: organization.bin ?? "",
        phone: organization.phone ?? "",
        email: organization.email ?? "",
        katoCode: organization.katoCode ?? "",
      });
      setLocality(
        organization.katoCode
          ? { kato: organization.katoCode, nameRu: organization.katoName || organization.katoCode }
          : { kato: "" }
      );
    } else {
      setForm(EMPTY);
      setLocality({ kato: "" });
    }
  }, [visible, organization]);

  const onSearch = React.useCallback(
    (value) => agent.Kato.search(value).then((res) => setOptions(Array.isArray(res) ? res : [])),
    []
  );

  const onLocality = (value) => {
    setLocality(value);
    setForm((prev) => ({ ...prev, katoCode: value?.kato || "" }));
  };

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const errors = validateOrganization(form, { isCreate });
  const shownErrors = touched ? errors : {};

  const handleSave = () => {
    setTouched(true);
    if (Object.keys(errors).length) return;
    onSubmit(toRequest(form, { isCreate }));
  };

  const field = (name, label, input, hint) => (
    <div className="p-field" style={{ marginBottom: 12 }}>
      <label style={{ display: "block", marginBottom: 4 }}>{label}</label>
      {input}
      {shownErrors[name] ? (
        <small style={{ color: "#dc2626" }}>{shownErrors[name]}</small>
      ) : (
        hint && <small style={{ color: "#6b7280" }}>{hint}</small>
      )}
    </div>
  );

  const invalid = (name) => (shownErrors[name] ? "p-invalid" : undefined);

  const footer = (
    <div>
      <Button label="Отмена" icon="pi pi-times" className="p-button-text" onClick={onHide} />
      <Button
        label={isCreate ? "Завести и выпустить ключ" : "Сохранить"}
        icon="pi pi-check"
        loading={loading}
        onClick={handleSave}
      />
    </div>
  );

  return (
    <Dialog
      header={isCreate ? "Новая организация" : `Организация ${organization?.companyName}`}
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: "560px", maxWidth: "95vw" }}
    >
      {isCreate
        ? field(
            "companyName",
            "Код организации",
            <InputText
              value={form.companyName}
              onChange={set("companyName")}
              className={invalid("companyName")}
              placeholder="например, tender_plus"
              style={{ width: "100%" }}
            />,
            "Латиница, цифры и _. После создания не меняется."
          )
        : field("companyName", "Код организации", <div style={{ fontWeight: 600 }}>{form.companyName}</div>)}

      {field(
        "bin",
        "БИН",
        <InputText
          value={form.bin}
          onChange={set("bin")}
          className={invalid("bin")}
          keyfilter="int"
          maxLength={12}
          style={{ width: "100%" }}
        />
      )}

      {field(
        "phone",
        "Телефон",
        <InputText value={form.phone} onChange={set("phone")} className={invalid("phone")} style={{ width: "100%" }} />
      )}

      {field(
        "email",
        "Email",
        <InputText value={form.email} onChange={set("email")} className={invalid("email")} style={{ width: "100%" }} />,
        "Необязательно"
      )}

      {field(
        "katoCode",
        "Населённый пункт",
        <SearchSelect
          placeholder="Начните вводить название"
          name="organizationLocality"
          state={locality}
          setState={onLocality}
          options={options}
          optionValue="kato"
          optionText="nameRu"
          onChange={onSearch}
          highlightRequired={Boolean(shownErrors.katoCode)}
        />,
        locality.kato ? `КАТО: ${locality.kato}` : undefined
      )}
    </Dialog>
  );
};

export default OrganizationDialog;
